<template>
	<canvas
		ref="overlay"
		class="overlay-canvas"
		:width="size"
		:height="size"
		:style="{ top: `${top}px`, left: `${left}px` }"
	></canvas>
</template>

<script>
export default {
	name: "VCrosshairs",

	props: {
		left: { type: Number, default: 0 },
		top: { type: Number, default: 0 },
		color: { type: String, default: "rgb(0, 0, 0)" },
		lineWidth: { type: Number, default: 1 },
		size: { type: Number, default: 50 },
		pulse: { type: Boolean, default: false },
		pulseColor: { type: String, default: "rgba(255, 23, 68, 0.8)" },
		pulseDuration: { type: Number, default: 900 },
	},

	data() {
		return {
			ctx: null,
			rafId: null,
			animationStart: 0,
			remainingPulses: 0,
		};
	},

	mounted() {
		this.ctx = this.$refs.overlay.getContext("2d");
	},

	beforeUnmount() {
		if (this.rafId) cancelAnimationFrame(this.rafId);
	},

	methods: {
		clear() {
			this.ctx = null;
		},
		clearCrosshairs() {
			this.ctx.clearRect(
				0,
				0,
				this.$refs.overlay.width,
				this.$refs.overlay.height,
			);
		},
		drawCrosshairs() {
			this.clearCrosshairs();
			this.drawBaseCrosshair();
		},

		drawBaseCrosshair() {
			if (!this.ctx) return;
			const ctx = this.ctx;
			const S = this.size;
			const center = S / 2;

			ctx.save();
			ctx.lineWidth = this.lineWidth ?? 1;
			ctx.strokeStyle = this.color ?? "rgba(255, 0, 0, 1)";

			ctx.beginPath();
			// horizontal
			ctx.moveTo(0, center);
			ctx.lineTo(S, center);
			// vertical
			ctx.moveTo(center, 0);
			ctx.lineTo(center, S);
			ctx.stroke();

			// center dot
			ctx.beginPath();
			ctx.shadowBlur = 0;
			ctx.fillStyle = this.color ?? "rgba(255, 0, 0, 1)";
			ctx.arc(center, center, Math.max(2, this.lineWidth + 1), 0, Math.PI * 2);
			ctx.fill();
			ctx.restore();
		},

		playPulse(repetitions = 1) {
			if (!this.ctx) return;
			if (this.rafId) cancelAnimationFrame(this.rafId);
			this.animationStart = 0;
			this.remainingPulses = Math.max(1, repetitions);
			this.rafId = requestAnimationFrame(this.animate);
		},

		animate(ts) {
			if (!this.animationStart) this.animationStart = ts;
			const elapsed = ts - this.animationStart;
			const duration = this.pulseDuration;
			const t = Math.min(1, elapsed / duration);

			// redraw base
			this.clearCrosshairs();
			this.drawBaseCrosshair();

			// pulsing ring
			const ctx = this.ctx;
			const S = this.size;
			const center = S / 2;
			const startRadius = S * 0.15;
			const endRadius = S * 0.5;
			const radius = startRadius + (endRadius - startRadius) * t;
			ctx.save();
			ctx.globalAlpha = 1 - t;
			ctx.lineWidth = 2 + (1 - t) * 2;
			ctx.strokeStyle = this.pulseColor;
			ctx.beginPath();
			ctx.arc(center, center, radius, 0, Math.PI * 2);
			ctx.stroke();
			ctx.restore();

			if (t < 1) {
				this.rafId = requestAnimationFrame(this.animate);
				return;
			}

			// one pulse finished
			this.animationStart = 0;
			this.remainingPulses -= 1;
			if (this.remainingPulses > 0) {
				this.rafId = requestAnimationFrame(this.animate);
			} else {
				this.rafId = null;
				this.drawCrosshairs();
			}
		},
	},
};
</script>

<style scoped lang="scss">
.overlay-canvas {
	position: absolute;
	width: v-bind(size + "px");
	height: v-bind(size + "px");
	z-index: 1;
	pointer-events: none;
}
</style>
